import { DateConverter } from "@shared-helpers/dateConverter";
import { UrlHelper } from "@shared-helpers/urlHelper";

export class AppHelper {
  public static readonly requestVerificationTokenKey = "__RequestVerificationToken";

  // These didn't seem to work when external variable undefined

  //static isFunction(value: any): value is (...args: any[]) => any {
  //  return typeof value === 'function';
  //}

  //static isDefined(value: any): value is (...args: any[]) => any {
  //  return typeof value !== 'undefined';
  //}

  static compareForSortAscending<T>(expression1: T, expression2: T, nullsFirst: boolean = true): number {
    return this.compareForSortInternal<T>(expression1, expression2, true, nullsFirst);
  }

  static compareForSortDescending<T>(expression1: T, expression2: T, nullsFirst: boolean = false): number {
    return this.compareForSortInternal<T>(expression1, expression2, false, nullsFirst);
  }

  static compareForSortInternal<T>(expression1: T, expression2: T, ascending: boolean, nullsFirst: boolean): number {
    if (!this.isNullOrUndefined(expression1)) {
      // expression1 is not null
      if (!this.isNullOrUndefined(expression2)) {
        // expression2 is not null
        // Both expressions non null
        return (expression1 > expression2) ? (ascending ? 1 : -1) : ((expression1 < expression2) ? (ascending ? -1 : 1) : 0);
      }
      else {
        // expression2 is null
        return nullsFirst ? 1 : -1;
      }
    }
    else {
      // expression1 is null
      if (!this.isNullOrUndefined(expression2)) {
        // expression2 is not null
        return nullsFirst ? -1 : 1;
      }
      else {
        // expression2 is null
        // Both expressions null
        return 0;
      }
    }
  }

  // Used to filter an array and return only unique entries
  static arrayFilterDistinct<T>(value: T, index: number, array: T[]) {
    return array?.indexOf(value) === index;
  }

  // Split an array into two based on whether each item matches the provided condition or not
  static arrayPartition<T>(array: T[], filter: (item: T, index: number, array: T[]) => boolean) {
    let pass: T[] = [], fail: T[] = [];
    array.forEach((item, index, array) => (filter(item, index, array) ? pass : fail).push(item));
    return [pass, fail];
  }

  static arrayAddIfNotExists<T>(source: Array<T>, item: T): Array<T> {
    if (source) {
      if (!source.includes(item)) {
        // Item not included in source, add it
        source.push(item);
      }
      return source;
    }
    // Source empty, return new array with item in
    return [item];
  }

  static arrayRemoveIfExists<T>(source: Array<T>, item: T): Array<T> {
    if (source) {
      if (source.includes(item)) {
        // Item included in source, remove it
        source = source.filter(obj => obj !== item);
      }
      return source;
    }
    // Source empty, return new empty array
    return [];
  }

  static isNullOrUndefined(value: any): boolean {
    return (value === null) || (value === undefined);
  }

  // Simple comparison of all object properties to check for equality, not tested on complex cases.
  // Arrays will be handled if objects are in the same order.
  static objectsEqual(obj1: any, obj2: any): boolean {
    if (obj1 === obj2) {
      return true;
    }

    if (typeof obj1 !== 'object' || obj1 === null ||
      typeof obj2 !== 'object' || obj2 === null) {
      return false;
    }

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
      return false;
    }

    for (const key of keys1) {
      if (!obj2.hasOwnProperty(key)) {
        return false;
      }

      if (!AppHelper.objectsEqual(obj1[key], obj2[key])) {
        return false;
      }
    }

    return true;
  }

  // From: https://stackoverflow.com/questions/40929260/find-last-index-of-element-inside-array-by-certain-condition#answer-53187807
  /**
  * Returns the index of the last element in the array where predicate is true, and -1
  * otherwise.
  * @param array The source array to search in
  * @param predicate find calls predicate once for each element of the array, in descending
  * order, until it finds one where predicate returns true. If such an element is found,
  * findLastIndex immediately returns that element index. Otherwise, findLastIndex returns -1.
  */
  static findLastIndex<T>(array: Array<T>, predicate: (value: T, index: number, obj: T[]) => boolean): number {
    let l = array.length;
    while (l--) {
      if (predicate(array[l], l, array))
        return l;
    }
    return -1;
  }

  static cloneObject<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj));
  }

  static parseJsonObject<T>(obj: any): T {
    return DateConverter.stringsToDates(JSON.parse(obj));
  }

  static getImageMimeTypeFromFilename(filename: string): string {
    switch (filename?.substring(filename?.lastIndexOf("."))?.toLowerCase()) {
      case ".webp":
        return "image/webp";
      case ".jpg":
      case ".jpeg":
        return "image/jpeg";
      case ".png":
        return "image/png";
      default:
        return "image/jpeg";
    }
  }

  /* Returns rating to nearest 0.5, rounding up */
  private static calculateRoundedStarRating(rating: number): number {
    return (Math.ceil((rating ?? 0) * 2) / 2);
  }

  /* Returns SVG href to use for image of 5 stars */
  static getRatingStarsSvgId(rating: number): string {
    let ratingRoundedUp = this.calculateRoundedStarRating(rating);
    let stars = ratingRoundedUp.toString().split('.');

    if (stars.length == 1) {
      return `#icon-stars-${stars[0]}-0-star`;
    }
    else {
      return `#icon-stars-${stars[0]}-${stars[1]}-star`;
    }
  }

  /* Returns SVG href to use for single star that is part of a rating */
  static getCurrentRatingStarSvgId(overallRating: number, currentStar: number): string {
    let ratingRoundedUp = this.calculateRoundedStarRating(overallRating);
    let floor = Math.floor(ratingRoundedUp);
    let ceiling = Math.ceil(ratingRoundedUp);

    if (floor >= currentStar) {
      // Current star should be filled
      return "#icon-stars-single-star-filled";
    }
    else if (ceiling < currentStar) {
      // Current star should not be filled
      return "#icon-stars-single-star-empty";
    }
    else {
      // Only option left is half filled
      return "#icon-stars-single-star-half";
    }
  }

  static obscureSensitiveData(data: string): string {
    if (data) {
      return '*'.repeat(data.length);
    }
    return data;
  }

  static isTestEnvironment(): boolean {
    return UrlHelper.isCurrentUrlPath(UrlHelper.testPage);
  }

  // Very basic number parser, does not support any localisation.
  // Could be extended in future, but for now just ensures parsing of numbers with a comma as a thousand separator succeeds.
  static parseNumber(input: string): number {
    return parseFloat(input?.replace(/,/g, ''));
  }

  // Basic number validation. Checks if non null and if not NaN when converted to Number.
  static isNumber(input: string): boolean {
    return input && !Number.isNaN(Number(input));
  }

}
